import { Badge, Button, LearnMoreLink } from '@auctisync/ui';
import { formatCents, type Tables } from '@auctisync/shared';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

type Payment = Tables<'payments'>;

type FailedRow = Pick<
  Payment,
  | 'id'
  | 'invoice_id'
  | 'tenant_id'
  | 'amount_cents'
  | 'currency'
  | 'failure_code'
  | 'failure_message'
  | 'failed_at'
  | 'created_at'
> & {
  invoices: Pick<Tables<'invoices'>, 'id' | 'invoice_number' | 'status' | 'tenant_id'> | null;
};

type Bucket = {
  code: string;
  rows: FailedRow[];
  totalCents: number;
};

const WINDOW_OPTIONS = [
  { label: 'Last 24 hours', value: 1 },
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 30 days', value: 30 },
];

/**
 * /admin/invoices/failed-report — Module #9 Phase 2.
 *
 * Bucketed view of failed payment attempts grouped by Stripe decline
 * code. Per-bucket drill-down shows individual invoices with bidder
 * info + per-invoice quick links. Per-bucket recommended action (e.g.,
 * "send pay-now link" for insufficient_funds, "do NOT auto-retry" for
 * fraud_detected) drives the right ops behavior.
 */
export function AdminInvoiceFailedReport() {
  const [windowDays, setWindowDays] = useState(1);
  const [rows, setRows] = useState<FailedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const since = new Date(Date.now() - windowDays * 24 * 60 * 60 * 1000).toISOString();
      const { data, error: err } = await supabase
        .from('payments')
        .select(
          'id, invoice_id, tenant_id, amount_cents, currency, failure_code, failure_message, failed_at, created_at, invoices!payments_invoice_id_fkey(id, invoice_number, status, tenant_id)',
        )
        .eq('status', 'failed')
        .gte('failed_at', since)
        .order('failed_at', { ascending: false })
        .limit(500);
      if (cancelled) return;
      if (err) {
        setError(err.message);
        setRows([]);
      } else {
        setRows((data ?? []) as unknown as FailedRow[]);
        setError(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [windowDays]);

  const buckets: Bucket[] = useMemo(() => {
    const map = new Map<string, Bucket>();
    for (const r of rows) {
      const code = r.failure_code ?? 'unknown';
      const existing = map.get(code);
      if (existing) {
        existing.rows.push(r);
        existing.totalCents += r.amount_cents ?? 0;
      } else {
        map.set(code, { code, rows: [r], totalCents: r.amount_cents ?? 0 });
      }
    }
    return [...map.values()].sort((a, b) => b.rows.length - a.rows.length);
  }, [rows]);

  return (
    <div>
      <Link to="/admin/invoices" style={{ color: 'var(--brand-primary)', fontSize: '0.875rem' }}>
        ← All invoices
      </Link>

      <header style={{ marginTop: '0.5rem', marginBottom: '1.5rem' }}>
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '1.875rem',
            fontWeight: 700,
            color: 'var(--text)',
            margin: 0,
          }}
        >
          Failed payments report
        </h1>
        <p style={{ color: 'var(--text-muted)', marginTop: '0.25rem', fontSize: '0.95rem' }}>
          Grouped by Stripe decline code. Each bucket shows the recommended next action.{' '}
          <LearnMoreLink helpSlug="managing-failed-payments" newTab>
            Managing failed payments
          </LearnMoreLink>
        </p>
      </header>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {WINDOW_OPTIONS.map((opt) => (
          <Button
            key={opt.value}
            variant={windowDays === opt.value ? 'default' : 'ghost'}
            onClick={() => setWindowDays(opt.value)}
          >
            {opt.label}
          </Button>
        ))}
      </div>

      {error && (
        <div role="alert" style={errorStyle}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-muted)' }}>Loading…</div>
      ) : buckets.length === 0 ? (
        <div style={emptyStateStyle}>
          <p style={{ margin: 0 }}>No failed payments in the selected window.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {buckets.map((bucket) => (
            <BucketCard key={bucket.code} bucket={bucket} windowDays={windowDays} />
          ))}
        </div>
      )}
    </div>
  );
}

function BucketCard({ bucket, windowDays }: { bucket: Bucket; windowDays: number }) {
  const [expanded, setExpanded] = useState(false);
  const action = recommendedAction(bucket.code);
  const sample = bucket.rows[0];
  return (
    <section style={cardStyle}>
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>
            {humanFailureCode(bucket.code)}{' '}
            <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              ({bucket.code})
            </span>
          </h2>
          <p style={{ margin: '0.25rem 0 0', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            <strong>{bucket.rows.length}</strong> failure{bucket.rows.length === 1 ? '' : 's'} ·
            total at stake{' '}
            <strong>{formatCents(bucket.totalCents, sample?.currency ?? 'usd')}</strong>
          </p>
        </div>
        <Badge variant={action.severity === 'critical' ? 'error' : 'secondary'}>
          {action.severity}
        </Badge>
      </header>

      <p
        style={{
          margin: '0.75rem 0',
          padding: '0.5rem 0.75rem',
          borderRadius: 'var(--r-tile, 0.375rem)',
          background: 'var(--surface)',
          fontSize: '0.875rem',
        }}
      >
        <strong>Recommended:</strong> {action.advice}
      </p>

      <Button variant="ghost" onClick={() => setExpanded(!expanded)}>
        {expanded
          ? 'Hide invoices'
          : `Show ${bucket.rows.length} invoice${bucket.rows.length === 1 ? '' : 's'}`}
      </Button>

      {expanded && (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '0.75rem 0 0',
            display: 'grid',
            gap: '0.5rem',
          }}
        >
          {bucket.rows.map((row) => (
            <li
              key={row.id}
              style={{
                padding: '0.5rem 0.75rem',
                background: 'var(--surface)',
                borderRadius: 'var(--r-tile, 0.375rem)',
                border: '1px solid var(--admin-hairline, var(--border))',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <div>
                <Link
                  to={`/admin/invoices/${row.invoice_id}`}
                  style={{ color: 'var(--brand-primary)', fontWeight: 500, fontSize: '0.875rem' }}
                >
                  {row.invoices?.invoice_number ?? row.invoice_id.slice(0, 8)}
                </Link>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                  {row.failed_at
                    ? new Date(row.failed_at).toLocaleString()
                    : new Date(row.created_at).toLocaleString()}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>
                  {formatCents(row.amount_cents ?? 0, row.currency)}
                </div>
                {row.failure_message && (
                  <div
                    style={{
                      color: 'var(--text-muted)',
                      fontSize: '0.7rem',
                      maxWidth: '20rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={row.failure_message}
                  >
                    {row.failure_message}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <p style={{ marginTop: '0.5rem', color: 'var(--text-muted)', fontSize: '0.75rem' }}>
        Window: last {windowDays} day{windowDays === 1 ? '' : 's'}.
      </p>
    </section>
  );
}

interface RecommendedAction {
  severity: 'critical' | 'normal';
  advice: string;
}

function recommendedAction(code: string): RecommendedAction {
  switch (code) {
    case 'insufficient_funds':
      return {
        severity: 'normal',
        advice: 'Send pay-now link so the bidder can fund their account or use a different card.',
      };
    case 'fraudulent':
    case 'fraud_detected':
    case 'card_velocity_exceeded':
      return {
        severity: 'critical',
        advice:
          'Do NOT auto-retry. Contact the bidder by phone first. The card was flagged for fraud; retrying will compound the lock.',
      };
    case 'expired_card':
      return {
        severity: 'normal',
        advice: 'Send pay-now link asking the bidder to update their card on file.',
      };
    case 'incorrect_cvc':
      return {
        severity: 'normal',
        advice: 'Bidder likely fat-fingered the CVC. Send pay-now link to retry from their side.',
      };
    case 'do_not_honor':
    case 'generic_decline':
      return {
        severity: 'normal',
        advice:
          'Bank declined without a specific reason. Ask the bidder to call their bank, then resend pay-now link.',
      };
    case 'lost_card':
    case 'stolen_card':
    case 'pickup_card':
      return {
        severity: 'critical',
        advice:
          'Card reported lost / stolen. Do NOT retry. Contact the bidder via a verified channel.',
      };
    case 'authentication_required':
      return {
        severity: 'normal',
        advice: '3DS authentication needed. Send pay-now link so the bidder can authenticate.',
      };
    default:
      return {
        severity: 'normal',
        advice:
          'Investigate the failure message in Stripe. Send pay-now link if the issue is bidder-fixable.',
      };
  }
}

function humanFailureCode(code: string): string {
  switch (code) {
    case 'insufficient_funds':
      return 'Insufficient funds';
    case 'fraudulent':
    case 'fraud_detected':
      return 'Fraud detected';
    case 'do_not_honor':
      return 'Card declined (do_not_honor)';
    case 'expired_card':
      return 'Expired card';
    case 'incorrect_cvc':
      return 'Wrong CVC';
    case 'authentication_required':
      return '3DS authentication required';
    case 'lost_card':
      return 'Lost card';
    case 'stolen_card':
      return 'Stolen card';
    case 'pickup_card':
      return 'Pickup card';
    case 'card_velocity_exceeded':
      return 'Card velocity exceeded';
    case 'generic_decline':
      return 'Generic decline';
    case 'unknown':
      return 'Unknown';
    default:
      return code.replace(/_/g, ' ');
  }
}

const cardStyle: React.CSSProperties = {
  background: 'var(--surface-elev)',
  border: '1px solid var(--admin-hairline, var(--border))',
  borderRadius: 'var(--r-card, 0.5rem)',
  padding: '1rem 1.25rem',
};

const errorStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  borderRadius: 'var(--r-tile, 0.375rem)',
  background: 'var(--error-bg, #fee2e2)',
  color: 'var(--error-fg, #991b1b)',
  marginBottom: '1rem',
};

const emptyStateStyle: React.CSSProperties = {
  padding: '1.5rem',
  borderRadius: 'var(--r-card, 0.5rem)',
  border: '1px dashed var(--border)',
  color: 'var(--text-muted)',
};
